import { useState } from 'react'
import { LuTerminal, LuAppWindowMac, LuMaximize2, LuX } from 'react-icons/lu'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import useServices from '../models/useServices'
import useTerminal from '../models/useTerminal'
import IconMinimize from './IconMinimize'
import SideMenuStack from './SideMenuStack'

export default function TerminalsTray() {
  const services = useServices(state => state.services)
  const { sessions, focusSession, setMode, requestClose } = useTerminal()
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null)
  const [iconHoveredSessionId, setIconHoveredSessionId] = useState<string | null>(null)

  if (!sessions.length) return null

  const labels = sessions.map(session => {
    const service = services.find(item => item.name === session.serviceName)
    const baseLabel = service ? service.title || service.name : session.serviceName
    // Two facts, not two names for the same one: the tray is the only place a
    // user sees what a LIVE session runs as, and joining with || meant a root
    // terminal running bash labelled itself "bash".
    const detail = [service?.command, service?.admin ? 'root' : ''].filter(Boolean).join(' · ')
    return { session, baseLabel, detail }
  })

  const shownCounts: Record<string, number> = {}

  return (
    <SideMenuStack align="bottom" reverse>
      {labels.map(({ session, baseLabel, detail }) => {
        shownCounts[baseLabel] = (shownCounts[baseLabel] || 0) + 1
        const showDetail = shownCounts[baseLabel] > 1
        const label = showDetail && detail ? `${baseLabel} • ${detail}` : baseLabel
        return (
          <Button
            key={session.id}
            onClick={() => {
              if (session.mode === 'minimized') {
                focusSession(session.id, 'modal')
              } else {
                setMode(session.id, 'minimized')
              }
            }}
            onMouseEnter={() => setHoveredSessionId(session.id)}
            onMouseLeave={() => {
              setHoveredSessionId(current => (current === session.id ? null : current))
              setIconHoveredSessionId(current => (current === session.id ? null : current))
            }}
          >
            <Typography variant="caption" color="textSecondary">
              {label}
            </Typography>
            <Stack
              onMouseEnter={() => setIconHoveredSessionId(session.id)}
              onMouseLeave={() => setIconHoveredSessionId(current => (current === session.id ? null : current))}
              onClick={event => {
                if (iconHoveredSessionId !== session.id) return
                event.stopPropagation()
                requestClose(session.id)
              }}
              sx={{
                width: 24,
                height: 24,
                alignItems: 'center',
                justifyContent: 'center',
                color: 'contrast.medium'
              }}>
              {iconHoveredSessionId === session.id ? (
                <LuX fontSize={16} />
              ) : hoveredSessionId === session.id ? (
                session.mode === 'minimized' ? (
                  session.previousMode === 'fullscreen' ? (
                    <LuMaximize2 fontSize={16} />
                  ) : (
                    <LuAppWindowMac fontSize={16} />
                  )
                ) : (
                  <IconMinimize sx={{ fontSize: 16 }} />
                )
              ) : (
                <LuTerminal fontSize={16} />
              )}
            </Stack>
          </Button>
        )
      })}
    </SideMenuStack>
  )
}
